import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { toast } from 'sonner';

export type TaskType = 'synthesis' | 'refinement' | 'launchKit';

export interface TaskState {
  id: string;
  type: TaskType;
  status: 'idle' | 'running' | 'completed' | 'failed';
  progress: number;
  startTime: number;
  metadata?: any;
  result?: any;
  error?: string;
}

interface TaskContextType {
  tasks: Record<TaskType, TaskState>;
  startTask: (type: TaskType, metadata?: any) => string;
  updateTask: (type: TaskType, updates: Partial<TaskState>) => void;
  completeTask: (type: TaskType, result?: any) => void;
  failTask: (type: TaskType, error: string) => void;
  resetTask: (type: TaskType) => void;
  isAnyTaskRunning: boolean;
}

const initialTaskState = (type: TaskType): TaskState => ({
  id: '',
  type,
  status: 'idle',
  progress: 0,
  startTime: 0,
});

const TaskContext = createContext<TaskContextType | undefined>(undefined);

export const TaskProvider = ({ children }: { children: ReactNode }) => {
  const [tasks, setTasks] = useState<Record<TaskType, TaskState>>({
    synthesis: initialTaskState('synthesis'),
    refinement: initialTaskState('refinement'),
    launchKit: initialTaskState('launchKit'),
  });

  const startTask = useCallback((type: TaskType, metadata?: any) => {
    const id = Math.random().toString(36).substring(7);
    setTasks(prev => ({
      ...prev,
      [type]: {
        id,
        type,
        status: 'running',
        progress: 0,
        startTime: Date.now(),
        metadata,
      }
    }));
    return id;
  }, []);

  const updateTask = useCallback((type: TaskType, updates: Partial<TaskState>) => {
    setTasks(prev => ({
      ...prev,
      [type]: { ...prev[type], ...updates }
    }));
  }, []);

  const completeTask = useCallback((type: TaskType, result?: any) => {
    setTasks(prev => ({
      ...prev,
      [type]: { 
        ...prev[type], 
        status: 'completed', 
        progress: 100, 
        result 
      }
    }));
    
    const labels: Record<TaskType, string> = {
      synthesis: 'Synthesis',
      refinement: 'Refinement',
      launchKit: 'Launch Kit'
    };
    
    toast.success(`${labels[type]} complete`, {
        description: "The background process has finished successfully."
    });
  }, []);

  const failTask = useCallback((type: TaskType, error: string) => {
    setTasks(prev => ({
      ...prev,
      [type]: { 
        ...prev[type], 
        status: 'failed', 
        error 
      }
    }));
    toast.error('Process failed', { description: error });
  }, []);

  const resetTask = useCallback((type: TaskType) => {
    setTasks(prev => ({
      ...prev,
      [type]: initialTaskState(type)
    }));
  }, []);

  const isAnyTaskRunning = Object.values(tasks).some(t => t.status === 'running');

  return (
    <TaskContext.Provider value={{
      tasks,
      startTask,
      updateTask,
      completeTask,
      failTask,
      resetTask,
      isAnyTaskRunning
    }}>
      {children}
    </TaskContext.Provider>
  );
};

export const useTasks = () => {
  const context = useContext(TaskContext);
  if (!context) throw new Error('useTasks must be used within TaskProvider');
  return context;
};
